import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    IconButton,
    Typography,
    Chip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    InputAdornment,
    FormControlLabel,
    Switch
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    FilterAlt as FilterIcon,
    Visibility as ViewIcon,
    CalendarToday as CalendarTodayIcon,
    CalendarMonth as CalendarIcon,
    Login as LoginIcon
} from '@mui/icons-material';
import flatpickr from "flatpickr";
import "flatpickr/dist/flatpickr.css";
import { format } from 'date-fns';
import Table from '../../components/Table/Table';
import UsersTable from '../users/UsersTable';
import TypeToConfirmDialog from "../../components/Dialogs/TypeToConfirmDialog";
import RecipientSelectionModal from './components/modals/RecipientSelectionModal';
import PresetPreviewDialog from './components/modals/PresetPreviewDialog';
import { MODAL_BORDER_RADIUS } from './utils/dateUtils';


import {
    useGetDeliveryRequestsQuery,
    useDeleteDeliveryRequestMutation,
    useAssignPresetRequestMutation
} from '../../store/services/delivery-service';
import {
    useGetTenancyBasicsQuery,
    useLazyGetCountriesByTenancyIdQuery,
    useLazyGetDepartmentsByTenancyIdQuery,
    useLazyGetLocationsByTenancyIdQuery,
    useLazyGetCitiesByTenancyIdQuery,
    useLazyGetStatesByTenancyIdQuery,
    useLazyGetCompanyNamesByTenancyIdQuery,
} from '../../store/services/tenancy.service';

const DeliveryRequestTable = ({ tenancyId, presetData, showNotification, onViewCalendar }) => {
    const navigate = useNavigate();

    const [paginationModel, setPaginationModel] = useState({
        pageSize: 100,
        page: 0
    });

    const [filters, setFilters] = useState({
        statusFilter: '',
        typeFilter: '',
        dateRangeStart: null,
        dateRangeEnd: null
    });

    const [filtersOpen, setFiltersOpen] = useState(false);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [dialogAction, setDialogAction] = useState(null);
    const [dialogTitle, setDialogTitle] = useState('');
    const [dialogContent, setDialogContent] = useState('');
    const [selectedRequestId, setSelectedRequestId] = useState(null);
    const [assignModalOpen, setAssignModalOpen] = useState(false);
    const [selectedPresetId, setSelectedPresetId] = useState('');
    const [startYearMonth, setStartYearMonth] = useState('');
    const [availableMonths, setAvailableMonths] = useState([]);
    const [isAdmin, setIsAdmin] = useState(false);
    const [adjustWeekends, setAdjustWeekends] = useState(false);
    const [recipientModalOpen, setRecipientModalOpen] = useState(false);
    const [presetRecipientModalOpen, setPresetRecipientModalOpen] = useState(false);
    const [pendingPresetData, setPendingPresetData] = useState(null);
    const [previewPreset, setPreviewPreset] = useState(null);


    // Refs for flatpickr
    const startDateRef = useRef(null);
    const endDateRef = useRef(null);
    const startDatePickerRef = useRef(null);
    const endDatePickerRef = useRef(null);

    useEffect(() => {
        // Generate a list of the next 24 months from now (excluding current month)
        const months = [];
        const now = new Date();
        const currentYear = now.getFullYear();
        const currentMonth = now.getMonth();

        // Generate 24 months starting from next month
        for (let i = 0; i <= 24; i++) {
            const targetMonth = (currentMonth + i) % 12;
            const targetYear = currentYear + Math.floor((currentMonth + i) / 12);
            const monthName = new Date(targetYear, targetMonth, 1).toLocaleString('default', { month: 'long' });

            months.push({
                value: `${targetYear}-${(targetMonth + 1).toString().padStart(2, '0')}`,
                label: `${monthName} ${targetYear}`
            });
        }

        setAvailableMonths(months);

        // Set default to next month
        if (months.length > 0) {
            setStartYearMonth(months[0].value);
        }
    }, []);


    useEffect(() => {
        const user = JSON.parse(localStorage.getItem('user'));
        setIsAdmin(user && user.role === "Administrators");
    }, []);

    const isStartDateValid = !!startYearMonth;

    // Initialize flatpickr
    useEffect(() => {
        // Date picker options
        const dateConfig = {
            enableTime: false,
            dateFormat: "Y-m-d",
            allowInput: true,
            onChange: ([date], dateStr, instance) => {
                if (date) {
                    const filterName = instance === startDatePickerRef.current
                        ? 'dateRangeStart'
                        : 'dateRangeEnd';
                    handleDateChange(filterName, date);
                }
            }
        };

        // Clean up previous instances
        if (startDatePickerRef.current) {
            startDatePickerRef.current.destroy();
        }
        if (endDatePickerRef.current) {
            endDatePickerRef.current.destroy();
        }

        // Initialize start date picker
        if (startDateRef.current) {
            startDatePickerRef.current = flatpickr(startDateRef.current, {
                ...dateConfig,
                defaultDate: filters.dateRangeStart
            });
        }

        // Initialize end date picker
        if (endDateRef.current) {
            endDatePickerRef.current = flatpickr(endDateRef.current, {
                ...dateConfig,
                defaultDate: filters.dateRangeEnd
            });
        }

        return () => {
            // Clean up flatpickr instances on unmount
            if (startDatePickerRef.current) {
                startDatePickerRef.current.destroy();
            }
            if (endDatePickerRef.current) {
                endDatePickerRef.current.destroy();
            }
        };
    }, [filtersOpen]);

    // Format dates for display
    const formatDate = (date) => {
        return date ? format(date, 'yyyy-MM-dd') : '';
    };

    // Query to get delivery requests
    const {
        data: deliveryData,
        isLoading,
        error,
        refetch
    } = useGetDeliveryRequestsQuery({
        tenancyId,
        pageSize: 100,
        page: 1,
        ...filters
    });

    // Create filtered data by removing cancelled requests
    const filteredData = React.useMemo(() => {
        if (!deliveryData) return null;

        const filteredRequests = deliveryData.requests.filter(req => req.status !== "Cancelled");

        return {
            ...deliveryData,
            requests: filteredRequests,
            totalRecords: deliveryData.totalRecords - (deliveryData.requests.length - filteredRequests.length)
        };
    }, [deliveryData]);

    // Get tenancy data for resolving group names
    const { data: tenancyData } = useGetTenancyBasicsQuery({ tenancyId });

    // Lazy queries for metadata types (for recipient name resolution)
    const [triggerCountries, { data: countriesData }] = useLazyGetCountriesByTenancyIdQuery();
    const [triggerDepartments, { data: departmentsData }] = useLazyGetDepartmentsByTenancyIdQuery();
    const [triggerLocations, { data: locationsData }] = useLazyGetLocationsByTenancyIdQuery();
    const [triggerCities, { data: citiesData }] = useLazyGetCitiesByTenancyIdQuery();
    const [triggerStates, { data: statesData }] = useLazyGetStatesByTenancyIdQuery();
    const [triggerCompanyNames, { data: companyNamesData }] = useLazyGetCompanyNamesByTenancyIdQuery();

    // Trigger metadata queries based on recipient types found in data
    useEffect(() => {
        if (filteredData?.items && tenancyId) {
            // Collect unique recipient types from all journeys
            const recipientTypes = new Set();
            Object.values(filteredData.items).forEach(items => {
                if (items?.length > 0) {
                    recipientTypes.add(items[0].recipientType);
                }
            });

            // Trigger appropriate queries
            if (recipientTypes.has('Country')) triggerCountries({ tenancyId });
            if (recipientTypes.has('Department')) triggerDepartments({ tenancyId });
            if (recipientTypes.has('Location')) triggerLocations({ tenancyId });
            if (recipientTypes.has('City')) triggerCities({ tenancyId });
            if (recipientTypes.has('State')) triggerStates({ tenancyId });
            if (recipientTypes.has('CompanyName')) triggerCompanyNames({ tenancyId });
        }
    }, [filteredData, tenancyId, triggerCountries, triggerDepartments, triggerLocations, triggerCities, triggerStates, triggerCompanyNames]);

    // Get the appropriate metadata based on recipient type
    const getMetaData = React.useCallback((type) => {
        switch (type) {
            case 'Country': return countriesData;
            case 'Department': return departmentsData;
            case 'Location': return locationsData;
            case 'City': return citiesData;
            case 'State': return statesData;
            case 'CompanyName': return companyNamesData;
            default: return null;
        }
    }, [countriesData, departmentsData, locationsData, citiesData, statesData, companyNamesData]);

    // Mutations
    const [deleteDeliveryRequest, { isLoading: isDeleting }] = useDeleteDeliveryRequestMutation();
    const [assignPresetRequest, { isLoading: isAssigning }] = useAssignPresetRequestMutation();

    // Helper function to get recipient display name
    const getRecipientDisplayName = React.useCallback((recipientType, sendToId) => {
        if (!recipientType) return '-';
        if (recipientType === 'Tenancy') return 'All Users';
        if (recipientType === 'User') return sendToId || '-';

        // For Group type (MS/Google synced groups), look up the group name from tenancy data
        if (recipientType === 'Group' && tenancyData?.groups) {
            const group = tenancyData.groups.find(g => String(g.userGroupId) === String(sendToId));
            if (group) return group.name;
        }

        // For metadata types, look up the name from the appropriate data
        const metaTypes = ['Department', 'Location', 'City', 'Country', 'State', 'CompanyName'];
        if (metaTypes.includes(recipientType)) {
            const metaData = getMetaData(recipientType);
            if (metaData) {
                const item = metaData.find(m => String(m.id) === String(sendToId));
                if (item) return item.name;
            }
            // Return loading indicator if data not yet loaded
            return `${recipientType}...`;
        }

        return sendToId || recipientType;
    }, [tenancyData, getMetaData]);

    // New function for calendar-based creation
    const handleCreateWithCalendar = () => {
        setRecipientModalOpen(true);
    };


    // Handle editing a delivery request
    const handleEditDeliveryRequest = (request) => {
        navigate(`/journeybuilder/edit/${tenancyId}/${request.id}`);
    };

    // Handle deleting a delivery request
    const handleDeleteDeliveryRequest = (requestId) => {
        setSelectedRequestId(requestId);
        setDialogTitle('Cancel Journey');
        setDialogContent('Are you sure you want to cancel this journey? This action is permanent and cannot be undone.');
        setDialogAction(() => async () => {
            try {
                await deleteDeliveryRequest({ tenancyId, requestId }).unwrap();
                showNotification('Journey cancelled successfully', 'success');
            } catch (err) {
                showNotification('Failed to cancel delivery journey: ' + (err.data?.message || 'Unknown error'), 'error');
            }
        });
        setConfirmDialogOpen(true);
    };

    // Handle viewing a delivery request
    const handleViewDeliveryRequest = (request) => {
        navigate(`/journeybuilder/view/${tenancyId}/${request.id}`);
    };

    // Handle viewing calendar for a delivery request
    const handleViewCalendar = (request) => {
        console.log("Navigating to calendar for request:", request.id);
        navigate(`/journeybuilder/calendar/${tenancyId}/${request.id}`);
    };

    const handleRecipientSelected = (recipientData) => {
        setRecipientModalOpen(false);
        // Navigate to calendar with recipient data
        navigate(`/journeybuilder/calendar-create/${tenancyId}`, {
            state: {
                recipient: recipientData
            }
        });
    };

    // Handle filter changes
    const handleFilterChange = (event) => {
        const { name, value } = event.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Handle date filter changes
    const handleDateChange = (name, date) => {
        setFilters(prev => ({
            ...prev,
            [name]: date ? format(date, 'yyyy-MM-dd') : null
        }));
    };

    // Reset filters
    const handleResetFilters = () => {
        setFilters({
            statusFilter: '',
            typeFilter: '',
            dateRangeStart: null,
            dateRangeEnd: null
        });

        // Reset flatpickr instances
        if (startDatePickerRef.current) {
            startDatePickerRef.current.clear();
        }
        if (endDatePickerRef.current) {
            endDatePickerRef.current.clear();
        }
    };

    // Open assign preset modal
    const handleOpenAssignModal = () => {
        setAssignModalOpen(true);
    };

    // Handle assign preset submission
    const handleAssignPreset = async () => {
        if (!selectedPresetId) {
            showNotification('Please select a preset template', 'error');
            return;
        }

        try {
            const assignData = {
                name: `Preset Delivery - ${new Date().toISOString().split('T')[0]}`,
                startDate: new Date().toISOString(),
                recipientType: 'Tenancy',
                sendToId: tenancyId.toString()
            };

            await assignPresetRequest({
                tenancyId,
                presetId: selectedPresetId,
                assignData
            }).unwrap();

            showNotification('Preset template assigned successfully', 'success');
            setAssignModalOpen(false);
            setSelectedPresetId('');
        } catch (err) {
            showNotification('Failed to assign preset template: ' + (err.data?.message || 'Unknown error'), 'error');
        }
    };

    // Handle opening recipient selection after template is selected
    const handleContinueToRecipients = () => {
        if (!selectedPresetId || !isStartDateValid) {
            showNotification('Please select a template and valid start month', 'error');
            return;
        }

        // Find the selected preset
        const selectedPreset = presetData?.presets?.find(preset => preset.id === selectedPresetId);

        if (!selectedPreset) {
            showNotification('Selected template not found', 'error');
            return;
        }

        // Create a date for the first day of the selected month
        const [year, month] = startYearMonth.split('-');
        const startDate = `${year}-${month}-01`; // First day of the month

        // Store pending preset data
        setPendingPresetData({
            preset: selectedPreset,
            startDate: startDate,
            adjustForWeekends: adjustWeekends
        });

        // Close template modal and open recipient selection
        setAssignModalOpen(false);
        setPresetRecipientModalOpen(true);
    };

    // Handle recipient selection for preset mode
    const handlePresetRecipientSelected = (recipientData) => {
        setPresetRecipientModalOpen(false);

        if (!pendingPresetData) {
            showNotification('No template selected', 'error');
            return;
        }

        // Navigate to calendar view with preset and recipient data
        navigate(`/journeybuilder/calendar-preset/${tenancyId}`, {
            state: {
                preset: pendingPresetData.preset,
                startDate: pendingPresetData.startDate,
                adjustForWeekends: pendingPresetData.adjustForWeekends,
                recipient: recipientData
            }
        });

        // Clear state
        setPendingPresetData(null);
        setSelectedPresetId('');
        setStartYearMonth('');
    };



    const statusChipColor = (status) => {
        switch (status) {
            case 'Pending': return 'warning';
            case 'InProgress': return 'secondary';
            case 'Completed': return 'primary';
            case 'Cancelled': return 'error';
            default: return 'default';
        }
    };

    // Table column definitions
    const headCells = [
        /*
        {
            id: "preview",
            label: "",
            render: (dataRow) => (
                <>
                    <Button
                        //                        onClick={() => handlePreview(dataRow)} 
                        size="small" variant="contained" color="warning">
                        Preview
                    </Button>
                </>
            ),
            initialVisible: true
        },
        {
            id: "edit",
            label: "",
            render: (dataRow) => {
                if (dataRow.tenancyId === 0 && !isAdmin) return <></>;

                return (
                    <Button
                        //onClick={() => handleEditContentPack(dataRow)}
                        size="small"
                        variant="contained"
                        color="secondary"
                    >
                        Edit
                    </Button>
                );
            },
            initialVisible: true
        },
        */
        // Calendar view button
        {
            id: "calendar",
            label: "",
            render: (dataRow) => (
                <Button
                    onClick={() => handleViewCalendar(dataRow)}
                    size="small"
                    variant="contained"
                    color="secondary"
                    sx={{ whiteSpace: "nowrap" }}
                >
                    Edit
                </Button>
            ),
            initialVisible: true
        },
        {
            id: "edit",
            label: "",
            render: (dataRow) => {
                return (
                    <Button
                        onClick={() => handleViewDeliveryRequest(dataRow)}
                        size="small"
                        variant="contained"
                        color="warning"
                    >
                        Overview
                    </Button>
                );
            },
            initialVisible: true
        },
        {
            id: 'name',
            label: 'Name',
            initialVisible: true
        },
        /*
        {
            id: 'status',
            label: 'Status',
            initialVisible: true,
            render: (row) => (
                <Chip
                    label={row.status === "InProgress" ? "In Progress" : row.status}
                    color={statusChipColor(row.status)}
                    size="small"
                />
            )
        },*/
        {
            id: 'items',
            label: 'Items',
            initialVisible: true,
            render: (row) => {
                const items = filteredData?.items?.[row.id] || [];
                return items.length;
            }
        },
        {
            id: 'recipient',
            label: 'Recipient',
            initialVisible: true,
            render: (row) => {
                const items = filteredData?.items?.[row.id] || [];
                if (items.length === 0) return '-';

                // Get recipient info from first item (all items in a journey share the same recipient)
                const firstItem = items[0];
                const displayName = getRecipientDisplayName(firstItem.recipientType, firstItem.sendToId);

                return (
                    <Chip
                        label={displayName}
                        size="small"
                        variant="outlined"
                        color={firstItem.recipientType === 'Tenancy' ? 'primary' : 'secondary'}
                        sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}
                    />
                );
            }
        },
        {
            id: 'createdAt',
            label: 'Created ',
            initialVisible: true,
            render: (row) => new Date(row.createdAt).toLocaleDateString()
        },
        {
            id: 'updatedAt',
            label: 'Last Updated',
            initialVisible: true,
            render: (row) => new Date(row.updatedAt).toLocaleDateString()
        },
        {
            id: "action",
            label: "",
            render: (dataRow) => {
                return (
                    <Button
                        variant="contained"
                        size="small"
                        color="error"
                        onClick={() => handleDeleteDeliveryRequest(dataRow.id)}
                        disabled={isDeleting && selectedRequestId === dataRow.id}
                        sx={{
                            minWidth: '70px',
                            width: '70px',
                            height: '32px',
                            padding: '6px 8px',
                            '& .MuiCircularProgress-root': {
                                marginRight: 0
                            }
                        }}
                    >
                        {isDeleting && selectedRequestId === dataRow.id ? (
                            <CircularProgress size={20} color="inherit" />
                        ) : (
                            "Cancel"
                        )}
                    </Button>
                );
            },
            initialVisible: true
        }
        /*
        {
            id: "action",
            label: "",
            render: (dataRow) => {
                return (
                    <Button
                        variant="contained"
                        size="small"
                        color="error"
                        onClick={() => handleDeleteDeliveryRequest(dataRow.id)}
                        disabled={isDeleting && selectedRequestId === dataRow.id}
                        sx={{
                            minWidth: '70px',
                            width: '70px',
                            height: '32px',
                            padding: '6px 8px',
                            '& .MuiCircularProgress-root': {
                                marginRight: 0
                            }
                        }}
                    >

                        {isDeleting && selectedRequestId === dataRow.id ? (
                            <CircularProgress size={20} color="inherit" />
                        ) : (
                            "Delete"
                        )}
                    </Button>
                );
            },
            initialVisible: true
        }
        */
    ];

    // Toolbar actions
    const toolbarActions = [
        {
            render: () => (
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                        variant="contained"
                        color="primary"
                        endIcon={<AddIcon />}
                        onClick={handleCreateWithCalendar}
                    >
                        Create Custom
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        endIcon={<CalendarIcon />}
                        onClick={handleOpenAssignModal}
                    >
                        Use Template
                    </Button>
                </Box>
            )
        }
    ];

    return (
        <>

            <Table
                name="Delivery Requests"
                data={filteredData?.requests || []}
                error={error}
                loading={isLoading}
                headCells={headCells}
                search={true}
                selected={[]}
                setSelected={() => { }}
                checkbox={false}
                toolbarActions={toolbarActions}
                showToolbarActions={true}
                maxTableHeight="calc(100vh - 360px)"
                paginationModel={paginationModel}
                setPaginationModel={setPaginationModel}
                rowCount={filteredData?.totalRecords || 0}
                initialVisibleColumns={headCells.filter(cell => cell.initialVisible).map(cell => cell.label)}
            />

            {/* Assign Preset Modal */}
            <Dialog
                open={assignModalOpen}
                onClose={() => setAssignModalOpen(false)}
                PaperProps={{
                    sx: {
                        bgcolor: 'background.default',
                        borderRadius: MODAL_BORDER_RADIUS
                    }
                }}
            >
                <DialogTitle sx={{ bgcolor: 'background.default' }}>Select Journey Template</DialogTitle>
                <DialogContent sx={{ minWidth: 450, bgcolor: 'background.default' }}>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel>Select Template</InputLabel>
                                <Select
                                    value={selectedPresetId}
                                    label="Select Template"
                                    onChange={(e) => setSelectedPresetId(e.target.value)}
                                >
                                    {presetData?.presets?.map((preset) => (
                                        <MenuItem key={preset.id} value={preset.id}>
                                            {preset.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel>Start Month</InputLabel>
                                <Select
                                    value={startYearMonth}
                                    label="Start Month"
                                    onChange={(e) => setStartYearMonth(e.target.value)}
                                >
                                    {availableMonths.map((month) => (
                                        <MenuItem key={month.value} value={month.value}>
                                            {month.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                Select the month when your journey should begin. Content will be scheduled based on the template's relative day and month values.
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={adjustWeekends}
                                        onChange={(e) => setAdjustWeekends(e.target.checked)}
                                    />
                                }
                                label="Move weekend items to Monday"
                            />
                        </Grid>
                        {selectedPresetId && presetData?.presets && (
                            <Grid item xs={12}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                    <Typography variant="body2">
                                        This template contains {presetData.presets.find(p => p.id === selectedPresetId)?.items?.length || 0} items
                                        that will be scheduled starting from the first day of {availableMonths.find(m => m.value === startYearMonth)?.label || 'the selected month'}.
                                    </Typography>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<ViewIcon />}
                                        onClick={() => setPreviewPreset(presetData.presets.find(p => p.id === selectedPresetId))}
                                        sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                                    >
                                        Preview
                                    </Button>
                                </Box>
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                    After creation, you'll be able to adjust specific dates in the calendar view.
                                </Typography>
                            </Grid>
                        )}

                    </Grid>
                </DialogContent>
                <DialogActions sx={{ bgcolor: 'background.default', p: 2, gap: 1 }}>
                    <Button variant="contained" color="secondary" onClick={() => setAssignModalOpen(false)}>Cancel</Button>
                    <Button
                        onClick={handleContinueToRecipients}
                        variant="contained"
                        disabled={!selectedPresetId || !isStartDateValid}
                    >
                        Continue
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Recipient Selection Modal for Preset Mode */}
            <RecipientSelectionModal
                open={presetRecipientModalOpen}
                onClose={() => {
                    setPresetRecipientModalOpen(false);
                    setPendingPresetData(null);
                }}
                onConfirm={handlePresetRecipientSelected}
            />

            <RecipientSelectionModal
                open={recipientModalOpen}
                onClose={() => setRecipientModalOpen(false)}
                onConfirm={handleRecipientSelected}
            />

            {/* Preset Preview Dialog */}
            <PresetPreviewDialog
                open={!!previewPreset}
                onClose={() => setPreviewPreset(null)}
                preset={previewPreset}
            />

            {/* Type-to-Confirm Dialog */}
            <TypeToConfirmDialog
                open={confirmDialogOpen}
                title={dialogTitle}
                content={dialogContent}
                confirmationWord="cancel"
                onConfirm={() => {
                    setConfirmDialogOpen(false);
                    dialogAction?.();
                }}
                onCancel={() => setConfirmDialogOpen(false)}
            />
        </>
    );
};

DeliveryRequestTable.propTypes = {
    tenancyId: PropTypes.number.isRequired,
    showNotification: PropTypes.func.isRequired,
    onViewCalendar: PropTypes.func
};

export default DeliveryRequestTable;